import axios from "axios";
import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";

interface WalletUser {
  uid: string;
  name: string;
  inr: number;
}


const Wallet: React.FC = () => {
  const [users, setUsers] = useState<WalletUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [debitValues, setDebitValues] = useState<Record<string, string>>({});
  const [creditValues, setCreditValues] = useState<Record<string, string>>({});

  /* ---------------- Fetch Wallet Data ---------------- */
  const fetchWallet = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);

    try {
      const res = await api.get(
        "/getwallet",
        {
          params: {
            page,
            limit,
            search: searchTerm,
          },
        }
      );

      setUsers(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Wallet fetch error:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, [page, searchTerm]);

  /* ---------------- Input Handling ---------------- */
  const handleInputChange = (
    uid: string,
    value: string,
    type: "debit" | "credit"
  ) => {
    if (type === "debit") {
      setDebitValues((prev) => ({ ...prev, [uid]: value }));
    } else {
      setCreditValues((prev) => ({ ...prev, [uid]: value }));
    }
  };

  /* ---------------- Debit / Credit ---------------- */
  const handleBalanceUpdate = async (
    uid: string,
    amount: string,
    type: "debit" | "credit"
  ) => {
    const token = localStorage.getItem("token");
    if (!token || !amount) return;

    try {
      await api.post(
        "/updatebalance",
        {
          uid,
          coin: "inr",
          amount,
          type,
        },
        { headers: { Authorization: token } }
      );

      fetchWallet();

      setDebitValues((prev) => ({ ...prev, [uid]: "" }));
      setCreditValues((prev) => ({ ...prev, [uid]: "" }));
    } catch (error) {
      console.error("Balance update error:", error);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Bonus and Penalty
          </h1>
          <p className="text-sm text-gray-500">
            Manage bonus and penalty for users.
          </p>
        </div>
        <span className="text-sm text-gray-500">
          Total Users: {total}
        </span>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <input
          className="w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-indigo-500"
          placeholder="Search by name or UID"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              {[
                "S.No",
                "Name",
                "UID",
                "Currency",
                "Balance",
                "Penalty",
                "Bonus",
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-gray-500">
                  Loading wallet data...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr
                  key={user.uid}
                  className="border-t hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    {(page - 1) * limit + index + 1}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {user.name}
                  </td>
                  <td className="px-4 py-3">{user.uid}</td>
                  <td className="px-4 py-3 font-semibold">৳</td>
                  <td className="px-4 py-3 text-green-600 font-semibold">
                    {formatCurrency(user.inr)}
                  </td>

                  {/* Debit */}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <input
                        className="w-24 border rounded px-2 py-1"
                        placeholder="Amount"
                        value={debitValues[user.uid] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            user.uid,
                            e.target.value,
                            "debit"
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          handleBalanceUpdate(
                            user.uid,
                            debitValues[user.uid],
                            "withdraw"
                          )
                        }
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                      >
                        Penalty
                      </button>
                    </div>
                  </td>

                  {/* Credit */}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <input
                        className="w-24 border rounded px-2 py-1"
                        placeholder="Amount"
                        value={creditValues[user.uid] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            user.uid,
                            e.target.value,
                            "credit"
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          handleBalanceUpdate(
                            user.uid,
                            creditValues[user.uid],
                            "deposit"
                          )
                        }
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                      >
                        Bonus
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>

          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
